import React, { useState, useMemo } from "react";
import {
    Box, Typography, Select, MenuItem, Table,
    TableHead, TableRow, TableCell, TableBody, IconButton,
    Pagination, Button, Menu, ListItemIcon, ListItemText,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import {
    PersonAdd, MoreHorizOutlined, EditOutlined, DeleteOutline, CopyAll
} from "@mui/icons-material";
import api from "../../../services/Axios";
import { useNavigate } from "react-router-dom";
import AdminPageHeader, { PageSpinner } from "../../../components/AdminPageHeader";
import { patchAdminData, useAdminData } from "../../../services/adminDataStore";
import { CopyCredentialsButton, copyToClipboard } from "./CreatorCredentialsCard";
import {
    credentialsFromCreator,
    formatCreatorCredentials,
} from "./creatorCredentials";

export default function AllCreators() {
    const navigator = useNavigate();
    const { creatorList, loaded } = useAdminData();
    const creators = creatorList || [];
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [copiedAll, setCopiedAll] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleMenuOpen = (e, row) => {
        setMenuAnchor(e.currentTarget);
        setMenuRow(row);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuRow(null);
    };

    const handleEdit = () => {
        const row = menuRow;
        handleMenuClose();
        if (!row) return;
        navigator("/user/creator-onboarding", {
            state: { userId: row.user_id || null, username: row.username },
        });
    };

    const handleCopyAllCredentials = async () => {
        const text = filtered
            .map((row) => formatCreatorCredentials(credentialsFromCreator(row)))
            .filter(Boolean)
            .join("\n\n");
        const ok = await copyToClipboard(text);
        if (!ok) return;
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 1500);
    };

    const handleDeleteClick = () => {
        const row = menuRow;
        handleMenuClose();
        if (!row) return;
        setDeleteTarget(row);
    };

    const handleDeleteCancel = () => {
        if (deleting) return;
        setDeleteTarget(null);
    };

    const handleDeleteConfirm = async () => {
        const row = deleteTarget;
        if (!row) return;
        setDeleting(true);
        try {
            await api.patch(`/admin/creators/${row.user_id}/disable`);
            patchAdminData((prev) => ({
                ...prev,
                creatorList: (prev.creatorList || []).filter((c) => c.user_id !== row.user_id),
            }));
            setDeleteTarget(null);
        } catch (err) {
            console.error("Delete creator failed:", err?.response?.status, err?.response?.data);
            alert(err?.response?.data?.detail || "Failed to delete creator.");
        } finally {
            setDeleting(false);
        }
    };

    const filtered = useMemo(() => {
        let data = [...creators];
        if (search) {
            const q = search.toLowerCase();
            data = data.filter((r) =>
                r.username?.toLowerCase().includes(q) ||
                `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase().includes(q)
            );
        }
        if (statusFilter) data = data.filter((r) => r.status === statusFilter);

        data.sort((a, b) => {
            switch (sortBy) {
                case "mrr": return (b.mrr ?? 0) - (a.mrr ?? 0);
                case "fans": return (b.fans ?? 0) - (a.fans ?? 0);
                case "subs": return (b.subs ?? 0) - (a.subs ?? 0);
                case "msgs": return (b.total_fan_msgs ?? 0) - (a.total_fan_msgs ?? 0);
                default: return a.username?.localeCompare(b.username);
            }
        });
        return data;
    }, [creators, search, statusFilter, sortBy]);

    const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const pageCount = Math.ceil(filtered.length / rowsPerPage) || 1;

    return (
        <Box className="dashboard-main">
            <Box maxWidth={1600} mx="auto">
                <AdminPageHeader
                    title="Creators"
                    actions={
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Button
                                variant="outlined"
                                startIcon={<CopyAll />}
                                disabled={filtered.length === 0}
                                onClick={handleCopyAllCredentials}
                                sx={{
                                    textTransform: "none", fontWeight: 600,
                                    color: copiedAll ? "#16a34a" : "#374151",
                                    borderColor: "#E5E7EB",
                                    "&:hover": { borderColor: "#D1D5DB", background: "#F9FAFB" },
                                }}
                            >
                                {copiedAll ? "Copied" : "Copy all credentials"}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<PersonAdd />}
                                onClick={() => navigator("/user/creator-onboarding")}
                                sx={{
                                    textTransform: "none", fontWeight: 600,
                                    background: "linear-gradient(90deg, #FF2D92, #A855F7)",
                                    "&:hover": { background: "linear-gradient(90deg, #E0227D, #9333EA)" },
                                }}
                            >
                                Onboard Creator
                            </Button>
                        </Box>
                    }
                />
                {!loaded ? <PageSpinner /> : (
                    <>
                        <Box overflow="auto">
                            <Table size="small" sx={{ borderRadius: 2, overflow: "hidden", backgroundColor: "#fff" }}>
                                <TableHead>
                                    <TableRow>
                                        {["Creator", "Username", "Email", "Phone", "Creator Revenue Split %", "Credentials"].map((h) => (
                                            <TableCell key={h} sx={{ color: "#000", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{ color: "#000", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.map((row, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell>
                                                <Typography fontSize={13} color="#000">{row?.first_name} {row?.last_name}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>{row.username ?? "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>{row.email ?? "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>{row.phone_number ?? "-"}</TableCell>
                                            <TableCell sx={{ fontSize: 12 }}>{`${row.creator_split_percent}%` ?? "-"}</TableCell>
                                            <TableCell>
                                                <CopyCredentialsButton
                                                    credentials={credentialsFromCreator(row)}
                                                    label="Copy credentials"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                                                    <MoreHorizOutlined fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#888", fontSize: 13 }}>
                                                No creators found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <MenuItem onClick={handleEdit}>
                                <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleDeleteClick} sx={{ color: "#d32f2f" }}>
                                <ListItemIcon><DeleteOutline fontSize="small" sx={{ color: "#d32f2f" }} /></ListItemIcon>
                                <ListItemText>Delete</ListItemText>
                            </MenuItem>
                        </Menu>

                        <Dialog open={Boolean(deleteTarget)} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                            <DialogTitle sx={{ fontWeight: 700 }}>Delete creator?</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    This will permanently delete{" "}
                                    <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.username}). This can't be undone.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                                <Button onClick={handleDeleteCancel} disabled={deleting} sx={{ textTransform: "none", color: "#555" }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                    variant="contained"
                                    color="error"
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1.5}>
                            <Typography fontSize={13} color="#6B7280">
                                {filtered.length === 0 ? "0" : `${Math.min((page - 1) * rowsPerPage + 1, filtered.length)}–${Math.min(page * rowsPerPage, filtered.length)}`} of {filtered.length}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography fontSize={13} color="#6B7280">Rows</Typography>
                                <Select
                                    size="small"
                                    value={rowsPerPage}
                                    onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                                    sx={{
                                        fontSize: 13, height: 32, minWidth: 72,
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                                    }}
                                >
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                                <Pagination count={pageCount} page={page} onChange={(_, p) => setPage(p)} size="small"
                                    sx={{
                                        "& .MuiPaginationItem-root": { color: "#888" },
                                        "& .Mui-selected": { backgroundColor: "#2a2a2a !important", color: "#fff" }
                                    }} />
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
}